import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./authContext";

export type PlanType = "free" | "starter" | "pro" | "business" | "enterprise";
export type SubscriptionStatus = "active" | "trialing" | "past_due" | "canceled" | "incomplete";

export interface PlanLimits {
  projects: number;
  exports: number;
  aiQueries: number;
  teamMembers: number;
  customBranding: boolean;
  prioritySupport: boolean;
  analytics: boolean;
  apiAccess: boolean;
}

export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  // Free: basic permit checklist + survival-score preview only. No PDF.
  free: { projects: 1, exports: 0, aiQueries: 5, teamMembers: 1, customBranding: false, prioritySupport: false, analytics: false, apiAccess: false },
  // Starter is deprecated — treat any legacy 'starter' subscription as Free.
  // This row only exists to satisfy the typed Record; live users are normalized to 'free' below.
  starter: { projects: 1, exports: 0, aiQueries: 5, teamMembers: 1, customBranding: false, prioritySupport: false, analytics: false, apiAccess: false },
  // Pro: full Operations suite, Survival Score, unlimited cities, unlimited AI advisor.
  pro: { projects: 1, exports: 9999, aiQueries: 9999, teamMembers: 1, customBranding: true, prioritySupport: true, analytics: true, apiAccess: false },
  // Studio: everything in Pro + multi-location + team seats.
  business: { projects: 50, exports: 9999, aiQueries: 9999, teamMembers: 5, customBranding: true, prioritySupport: true, analytics: true, apiAccess: true },
  enterprise: { projects: 9999, exports: 9999, aiQueries: 9999, teamMembers: 9999, customBranding: true, prioritySupport: true, analytics: true, apiAccess: true },
};

// ─── Feature → minimum plan map ─────────────────────────────────────
// Single source of truth for which tier unlocks each capability.
// Use `hasFeatureAccess(plan, feature)` everywhere instead of hardcoding
// plan checks like `plan === "free"`.
export type FeatureKey =
  // Free tier
  | "permits.basic"          // basic required-permits checklist
  | "score.preview"          // survival-score preview (no real data entry)
  // Previously Starter tier — now included in Free
  | "openingPlan"            // full opening plan (timeline, milestones, staffing layout)
  | "permits.full"           // all permits incl. optional
  | "budget"                 // budget planner
  | "pdfExport"              // PDF roadmap download
  // Pro tier
  | "operations"             // post-opening Operations suite (P&L, cash flow, inventory…)
  | "survivalScore"          // live survival score + recommendations
  | "inventory"
  | "staffRota"
  | "healthInspection"
  | "leaseTracker"
  | "forecast"
  | "equipment"
  | "locations.unlimited"    // unlimited cities / location browsing
  | "aiAdvisor"              // AI assistant (unlimited)
  // Studio tier
  | "multiLocation"          // My Locations portfolio
  | "team";                  // team seats

export const FEATURE_MIN_PLAN: Record<FeatureKey, PlanType> = {
  "permits.basic": "free",
  "score.preview": "free",

  // Starter tier removed — these features now require Pro (Free is unchanged from before).
  openingPlan: "pro",
  "permits.full": "pro",
  budget: "pro",
  pdfExport: "pro",

  operations: "pro",
  survivalScore: "pro",
  inventory: "pro",
  staffRota: "pro",
  healthInspection: "pro",
  leaseTracker: "pro",
  forecast: "pro",
  equipment: "pro",
  "locations.unlimited": "pro",
  aiAdvisor: "pro",

  multiLocation: "business",
  team: "business",
};

const PLAN_RANK: Record<PlanType, number> = {
  // Starter is collapsed to the same rank as Free (deprecated tier).
  free: 0, starter: 0, pro: 1, business: 2, enterprise: 3,
};

export function planRank(plan: PlanType): number {
  return PLAN_RANK[plan] ?? 0;
}

/** Returns true when `plan` meets the minimum required for `feature`. */
export function hasFeatureAccess(plan: PlanType, feature: FeatureKey): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[FEATURE_MIN_PLAN[feature]];
}

export const PLAN_PRICES: Record<PlanType, { monthly: number; yearly: number }> = {
  free: { monthly: 0, yearly: 0 },
  // Source of truth lives in supabase/functions/create-checkout PRICE_TABLE.
  // Yearly figures here are the per-year sticker price (2 months free vs monthly × 12).
  // Starter is deprecated — kept at $0 so any legacy reference renders harmlessly.
  starter: { monthly: 0, yearly: 0 },
  pro: { monthly: 99, yearly: 990 },
  business: { monthly: 179, yearly: 1790 },
  enterprise: { monthly: 0, yearly: 0 },
};

export const PLAN_LABELS: Record<PlanType, string> = {
  free: "Free",
  // Legacy 'starter' rows are normalized to 'free' before display, but keep a label for safety.
  starter: "Free",
  pro: "Pro",
  business: "Studio",
  enterprise: "Enterprise",
};

export interface Subscription {
  id: string;
  plan: PlanType;
  status: SubscriptionStatus;
  billing_period: "monthly" | "yearly";
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  projects_used: number;
  exports_used: number;
  referral_code: string | null;
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description: string | null;
  invoice_date: string;
  pdf_url: string | null;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  invoices: Invoice[];
  loading: boolean;
  limits: PlanLimits;
  canUseFeature: (feature: keyof PlanLimits) => boolean;
  isWithinLimit: (usage: "projects" | "exports" | "aiQueries", current?: number) => boolean;
  hasFeature: (feature: FeatureKey) => boolean;
  plan: PlanType;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const ADMIN_EMAILS = ["enricarloni@gmail.com"];

export function isAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = isAdminEmail(user?.email);
  const effectiveSubscription: Subscription | null = isAdmin
    ? {
        id: subscription?.id ?? "admin-bypass",
        plan: "enterprise",
        status: "active",
        billing_period: subscription?.billing_period ?? "yearly",
        trial_ends_at: null,
        current_period_start: subscription?.current_period_start ?? null,
        current_period_end: subscription?.current_period_end ?? null,
        projects_used: 0,
        exports_used: 0,
        referral_code: subscription?.referral_code ?? null,
      }
    : subscription;

  const limits = effectiveSubscription ? PLAN_LIMITS[effectiveSubscription.plan] : PLAN_LIMITS.free;

  const fetchSubscription = useCallback(async () => {
    if (!user) { setSubscription(null); setInvoices([]); setLoading(false); return; }
    setLoading(true);
    const { data: sub } = await supabase.from("subscriptions").select("*").eq("user_id", user.id).maybeSingle();
    if (sub) {
      // Starter tier is deprecated — anyone still on it is treated as Free.
      const rawPlan = sub.plan as PlanType;
      const newPlan: PlanType = rawPlan === "starter" ? "free" : rawPlan;
      // Detect plan change vs previously loaded value.
      setSubscription((prev) => {
        if (prev && prev.plan !== newPlan) {
          const isCancellation = newPlan === "free" && prev.plan !== "free";
          const isUpgrade = newPlan !== "free";
          const key = `plan-change-${user.id}-${prev.plan}-to-${newPlan}`;
          const alreadySent = typeof window !== "undefined" && window.localStorage.getItem(key);
          if (!alreadySent) {
            try { window.localStorage.setItem(key, "1"); } catch {}
            const planLabel = PLAN_LABELS[newPlan] ?? newPlan;
            const prevPlanLabel = PLAN_LABELS[prev.plan] ?? prev.plan;
            const price = PLAN_PRICES[newPlan]?.monthly ?? 0;

            // 1. User-facing email — upgrade or cancellation.
            if (user.email) {
              if (isCancellation) {
                const endDate = sub.current_period_end
                  ? new Date(sub.current_period_end).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
                  : "the end of your billing period";
                supabase.functions.invoke("send-transactional-email", {
                  body: {
                    templateName: "cancellation",
                    recipientEmail: user.email,
                    idempotencyKey: key,
                    templateData: { planName: prevPlanLabel, endDate },
                  },
                }).catch(() => {});
              } else if (isUpgrade) {
                const billingDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
                supabase.functions.invoke("send-transactional-email", {
                  body: {
                    templateName: "upgrade",
                    recipientEmail: user.email,
                    idempotencyKey: key,
                    templateData: {
                      planName: planLabel,
                      planPrice: `$${price}`,
                      billingDate,
                      userEmail: user.email,
                    },
                  },
                }).catch(() => {});
              }
            }

            // 2. Admin notification copy.
            supabase.functions.invoke("send-transactional-email", {
              body: {
                templateName: "admin-signup-notification",
                recipientEmail: "enrico@oplif.com",
                idempotencyKey: `admin-${key}`,
                templateData: {
                  userEmail: user.email,
                  plan: newPlan,
                  eventType: isCancellation ? "cancellation" : "plan_change",
                  timestamp: new Date().toISOString(),
                },
              },
            }).catch(() => {});
          }
        }
        return {
          id: sub.id,
          plan: newPlan,
          status: sub.status as SubscriptionStatus,
          billing_period: sub.billing_period as "monthly" | "yearly",
          trial_ends_at: sub.trial_ends_at,
          current_period_start: sub.current_period_start,
          current_period_end: sub.current_period_end,
          projects_used: sub.projects_used,
          exports_used: sub.exports_used,
          referral_code: sub.referral_code,
        };
      });
    }
    const { data: inv } = await supabase.from("invoices").select("*").eq("user_id", user.id).order("invoice_date", { ascending: false });
    if (inv) setInvoices(inv as Invoice[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  // Realtime: when our `subscriptions` row is upserted by the stripe-webhook
  // (or verify-checkout-session), refresh immediately so feature gates
  // unlock without waiting for a manual reload.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`sub-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => { fetchSubscription(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchSubscription]);

  const canUseFeature = (feature: keyof PlanLimits) => {
    if (isAdmin) return true;
    const val = limits[feature];
    return typeof val === "boolean" ? val : true;
  };

  const isWithinLimit = (usage: "projects" | "exports" | "aiQueries", current?: number) => {
    if (isAdmin) return true;
    if (!effectiveSubscription) return false;
    const used = current ?? (usage === "projects" ? effectiveSubscription.projects_used : effectiveSubscription.exports_used);
    return used < limits[usage];
  };

  const currentPlan: PlanType = effectiveSubscription?.plan ?? "free";
  const hasFeature = (feature: FeatureKey) => {
    if (isAdmin) return true;
    return hasFeatureAccess(currentPlan, feature);
  };

  return (
    <SubscriptionContext.Provider value={{ subscription: effectiveSubscription, invoices, loading, limits, canUseFeature, isWithinLimit, hasFeature, plan: currentPlan, refreshSubscription: fetchSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}
